import { SyntheticEvent, useEffect, useState } from 'react';
import { shuffle } from 'lodash';
import { captureException } from '@sentry/nextjs';
import { css } from '@emotion/react';
import styled from '@emotion/styled';
import { useTrackingContext } from '@zapier/toolbox-tracking';
import { Zindexes } from '@zapier/theme';
import {
  Button,
  Colors,
  Icon,
  Surface,
  Text,
  Typography,
  LabeledRadio,
} from '@zapier/design-system';
import { mediaQueries } from '../../constants';
import { useViewportSize } from '../../hooks/useViewportSize';
import {
  EventAction,
  InteractionName,
  useAvroTracking,
} from '../../observability';
import { useIdentity } from '../../rest/zapier';
import { usePopupSurvey } from './usePopupSurvey';
import useLocalStorage from '../../hooks/useLocalStorage';
import { SplitioProvider } from '../SplitioProvider';

type OptionsShape = {
  id: string;
  label: string;
};

export const shuffledOptions: Array<OptionsShape> = shuffle([
  {
    id: 'search-engine',
    label: 'Search engine',
  },
  {
    id: 'facebook-instagram',
    label: 'Facebook/ Instagram',
  },
  {
    id: 'website-referral',
    label: 'Website Referrals',
  },
  {
    id: 'podcast',
    label: 'Podcast',
  },
  {
    id: 'word-of-mouth',
    label: 'Word of mouth',
  },
  {
    id: 'linkedin',
    label: 'LinkedIn',
  },
  {
    id: 'youtube',
    label: 'YouTube',
  },
  {
    id: 'billboard',
    label: 'Billboard',
  },
]);

const EXPERIMENT_NAME = 'popup-survey';
const SURVEY_EVENTS = {
  shown: 'shown',
  dismissed: 'dismissed',
  completed: 'completed',
};

export const Survey = () => {
  const popupSurvey = usePopupSurvey();
  const [showPopupFromLocalStorage, setShowPopupFromLocalStorage] =
    useLocalStorage('zapier__popup-survey');
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);

  const { data } = useIdentity();

  const { width } = useViewportSize();
  const { emitUserInteractionEvent } = useAvroTracking();

  const { trackEvent } = useTrackingContext();

  const isMobile = width === 'small' || width === 'extraSmall';
  const [selectedOption, setSelectedOption] = useState('');

  // Begin as collapsed for small only.
  const [isCollapsed, setIsCollapsed] = useState(isMobile);
  // Once expanded, it may not be collapsed again.
  const expandSurvey = () => {
    setIsCollapsed(false);
  };

  useEffect(() => {
    setIsCollapsed(isMobile);
  }, [isMobile]);

  useEffect(() => {
    emitUserInteractionEvent({
      event_action: EventAction.in_view,
      interaction_name: InteractionName.popup,
      option_selected: 'survey',
      custom_tracking_fields: {
        component: 'Survey',
      },
    });
    trackEvent(`${EXPERIMENT_NAME}:${SURVEY_EVENTS.shown}`);
  }, [emitUserInteractionEvent, trackEvent]);

  const options = [
    ...shuffledOptions,
    {
      id: 'other',
      label: 'Other',
    },
  ];

  const handleSelectOption = (e: SyntheticEvent) => {
    const target = e.target as HTMLInputElement;
    const value = target.value;
    setSelectedOption(value);
    emitUserInteractionEvent({
      event_action: EventAction.select,
      interaction_name: InteractionName.radio_select,
      interaction_raw_text: options.find((option) => option.id === target.id)
        ?.label,
      option_selected: 'radio',
      interaction_goal: 'survey',
      custom_tracking_fields: {
        component: 'Survey',
      },
    });
  };

  function handleSubmit(e: SyntheticEvent) {
    e.preventDefault();
    const option = options.find((option) => option.id === selectedOption);
    if (!option) return;
    try {
      trackEvent(`${EXPERIMENT_NAME}:${SURVEY_EVENTS.completed}`, {
        response: option.id,
      });
      emitUserInteractionEvent({
        event_action: EventAction.click,
        interaction_name: InteractionName.submit,
        interaction_raw_text: 'Submit',
        option_selected: 'submit',
        interaction_goal: 'survey',
        custom_tracking_fields: {
          responseID: option.id,
          responseLabel: option.label,
          component: 'Survey',
        },
      });
    } catch (error) {
      captureException(error);
    } finally {
      setShowSuccessMessage(true);
      setShowPopupFromLocalStorage(false);
    }
  }

  const handleDismissClick = () => {
    setShowSuccessMessage(false);
    setShowPopupFromLocalStorage(false);
    emitUserInteractionEvent({
      event_action: EventAction.close,
      interaction_name: InteractionName.dismiss,
      interaction_raw_text: 'Close the survey',
      option_selected: 'dismiss_button',
      interaction_goal: 'survey',
      custom_tracking_fields: {
        component: 'Survey',
      },
    });
    trackEvent(`${EXPERIMENT_NAME}:${SURVEY_EVENTS.dismissed}`);
  };

  const showSurvey =
    showSuccessMessage ||
    (showPopupFromLocalStorage === null &&
      data?.has_signed_up === false &&
      popupSurvey.isReady &&
      popupSurvey.isActive);

  if (!showSurvey) return null;

  return (
    <Container>
      <Surface backgroundColor="white" borderColor="gray20">
        <Content isCollapsed={isCollapsed}>
          <Header>
            <CloseButton onClick={handleDismissClick}>
              <Icon
                ariaLabel="Close the survey"
                color="gray60"
                name="formX"
                size={25}
              />
            </CloseButton>
            {!showSuccessMessage && (
              <Text fontWeight={700} tag="h2" type="paragraph1Short">
                What brought you to us today?
              </Text>
            )}
          </Header>
          {isCollapsed ? (
            <ExpandButton onClick={expandSurvey}>(tap to answer)</ExpandButton>
          ) : (
            <Form onSubmit={handleSubmit}>
              {showSuccessMessage ? (
                <SuccessWrapper>
                  <Icon color="success" name="formCheckCircle" size={60} />
                  <Text tag="h2" textAlign="center" type="pageHeader5">
                    Thank you
                  </Text>
                </SuccessWrapper>
              ) : (
                <ControlsWrapper>
                  <Fieldset>
                    {options.map((option) => (
                      <LabeledRadio
                        tabIndex={0}
                        key={option.id}
                        onChange={handleSelectOption}
                        name="mp-survey"
                        value={option.id}
                        id={option.id}
                        checked={option.id === selectedOption}
                      >
                        {option.label}
                      </LabeledRadio>
                    ))}
                  </Fieldset>
                </ControlsWrapper>
              )}
              <SubmitWrapper>
                {showSuccessMessage ? (
                  <Button
                    isFullWidth={true}
                    onClick={() => setShowSuccessMessage(false)}
                    size="small"
                    type="button"
                  >
                    Close
                  </Button>
                ) : (
                  <Button
                    disabled={!selectedOption}
                    isFullWidth={true}
                    size="small"
                  >
                    Submit
                  </Button>
                )}
              </SubmitWrapper>
            </Form>
          )}
        </Content>
      </Surface>
    </Container>
  );
};

const Container = styled.div`
  bottom: -8px;
  border-radius: 10px;
  box-shadow: 0 0 10px 5px rgba(0, 0, 0, 0.1);
  position: fixed;
  right: 50px;
  z-index: ${Zindexes.fixedSurvey};

  @media screen and (max-width: 565px) {
    right: 50%;
    transform: translateX(50%);
  },
`;

const Content = styled.div<{
  isCollapsed: boolean;
}>(({ isCollapsed }) => [
  css`
    position: relative;
    padding: 25px 15px 0;
    max-width: 95vw;
    width: 330px;
    padding-bottom: ${isCollapsed ? '25px' : 0};

    ${mediaQueries.tablet} {
      padding-bottom: 0;
    }
  `,
]);

const Header = styled.div`
  padding-top: 10px;
`;

const CloseButton = styled.div`
  position: absolute;
  right: 10px;
  top: 10px;

  &:hover {
    cursor: pointer;
  }
`;

const ExpandButton = styled.button`
  ${Typography.smallPrint1};
  color: ${Colors.blueJeans};
  margin-top: 5px;
  background-color: transparent;
  outline: none;
  border: none;
`;

const Form = styled.form`
  margin-top: 30px;
`;

const SuccessWrapper = styled.div`
  align-items: initial;
  display: grid;
  justify-items: center;
  min-height: 190px;
`;

const ControlsWrapper = styled.div`
  padding-bottom: 20px;
`;

const SubmitWrapper = styled.div`
  background-color: ${Colors.gray5};
  border-top: 1px solid ${Colors.gray20};
  margin-left: -15px;
  margin-right: -15px;
  padding: 20px 15px 33px 15px;
`;

const Fieldset = styled.fieldset`
  border: none;
  display: grid;
  align-items: center;
  grid-template-columns: 1fr 1fr;
  margin-bottom: 0;
  padding-left: 0;
`;

export const SurveyContainer = () => (
  <SplitioProvider>
    <Survey />
  </SplitioProvider>
);
